import { setAnalyticsContext } from '../../utils/analytics';
import { analyticsReady } from '../../utils/ready';
import { captureErrorAsync } from './../../utils/capture-error-async';
import { persistAttributionParams } from './ad-attribution';
import {
  TRAFFIC_INPUT_SELECTOR,
  attachToAllCompatibleForms,
  attachToFF8Form,
  fillTrafficInputs,
} from './form-attachment';
import { logger } from './logger';
import { setupSourceBuster } from './setup-source-buster';
import type { TrafficInfo } from './types';

type Variables = Record<string, never>;

/** The traffic info from the latest sourcebuster callback in this document. */
let storedTrafficInfo: TrafficInfo | undefined;

/** Watches for traffic inputs that render after the initial attachment. */
let formObserver: MutationObserver | undefined;

/** True when a mutation added a traffic input, directly or inside a subtree. */
function addsTrafficInput(mutations: MutationRecord[]): boolean {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (
        node instanceof Element &&
        (node.matches(TRAFFIC_INPUT_SELECTOR) ||
          node.querySelector(TRAFFIC_INPUT_SELECTOR))
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Fills traffic inputs that render later, such as the form on a page reached
 * through client-side navigation.
 */
function observeLateForms(): void {
  if (formObserver) return;

  formObserver = new MutationObserver((mutations) => {
    if (!storedTrafficInfo || !addsTrafficInput(mutations)) return;
    try {
      fillTrafficInputs(storedTrafficInfo);
    } catch (e) {
      captureErrorAsync(e as Error);
    }
  });
  formObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function setTrafficInfoInAnalyticsContext(trafficInfo: TrafficInfo) {
  setAnalyticsContext({
    campaign: {
      name: trafficInfo.campaign,
      source: trafficInfo.source,
      medium: trafficInfo.medium,
      content: trafficInfo.content,
      term: trafficInfo.term,
    },
  });
}

async function analyticsIdentify(trafficInfo: TrafficInfo) {
  await analyticsReady();

  setTrafficInfoInAnalyticsContext(trafficInfo);

  window.analytics!.identify({ trafficInfo }, window.analytics._defaultContext);
}

function afterCookiesSetup(trafficInfo: TrafficInfo) {
  storedTrafficInfo = trafficInfo;

  try {
    attachToAllCompatibleForms(trafficInfo);
  } catch (e) {
    logger.error(
      'Could not attach to compatible form (%s)',
      (e as Error).message
    );
    captureErrorAsync(e as Error);
  }

  try {
    attachToFF8Form(trafficInfo);
  } catch (e) {
    logger.error(
      'Could not attach to FF8 contact form (%s)',
      (e as Error).message
    );
    captureErrorAsync(e as Error);
  }

  try {
    analyticsIdentify(trafficInfo);
  } catch (e) {
    captureErrorAsync(e as Error);
  }
}

export function install(_: Variables): Promise<void> {
  observeLateForms();
  setupSourceBuster(afterCookiesSetup);
  return persistAttributionParams();
}
